import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiBell,
  FiSearch,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { noticeAPI } from "../../services/api";
import { formatDate } from "../../utils/helpers";
import { triggerRefresh } from "../../hooks/useDataRefresh";
import AdminLayout from "../../components/layout/AdminLayout";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const CATEGORIES = ["General", "Academic", "Sports", "Events", "Urgent", "Holiday", "Exam"];

const emptyForm = {
  title: "",
  content: "",
  category: "General",
  publishDate: "",
  expiryDate: "",
  isPublished: true,
};

const categoryColors = {
  Academic: "bg-blue-100 text-blue-700",
  Holiday: "bg-emerald-100 text-emerald-700",
  Exam: "bg-violet-100 text-violet-700",
  Events: "bg-orange-100 text-orange-700",
  General: "bg-slate-100 text-slate-600",
  Urgent: "bg-red-100 text-red-600",
  Sports: "bg-sky-100 text-sky-700",
};

const AdminNotices = () => {
  const [notices, setNotices] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    const fetchNotices = async () => {
      try {
        const response = await noticeAPI.getAdmin();
        const data = response.data?.data?.notices || [];
        setNotices(data);
        setFiltered(data);
      } catch {
        toast.error("Failed to load notices");
      } finally {
        setLoading(false);
      }
    };
    fetchNotices();
  }, []);

  useEffect(() => {
    const result = search.trim()
      ? notices.filter((n) => n.title.toLowerCase().includes(search.toLowerCase()))
      : notices;
    setFiltered(result);
  }, [search, notices]);

  const openCreate = () => { setEditItem(null); setForm(emptyForm); setModal(true); };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      title: item.title,
      content: item.content,
      category: item.category,
      publishDate: item.publishDate?.slice(0, 10) || "",
      expiryDate: item.expiryDate?.slice(0, 10) || "",
      isPublished: item.isPublished,
    });
    setModal(true);
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.content.trim()) {
      toast.error("Title and content are required");
      return;
    }
    setSaving(true);
    try {
      if (editItem) {
        const response = await noticeAPI.update(editItem._id, form);
        const updated = response.data?.data?.notice || { ...editItem, ...form };
        setNotices((prev) => prev.map((n) => (n._id === editItem._id ? updated : n)));
        toast.success("Notice updated");
        triggerRefresh("notices");
      } else {
        const response = await noticeAPI.create(form);
        const newItem = response.data?.data?.notice;
        if (newItem) setNotices((prev) => [newItem, ...prev]);
        toast.success("Notice created");
        triggerRefresh("notices");
      }
      setModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save notice");
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await noticeAPI.togglePublish(id);
      setNotices((prev) => prev.map((n) => n._id === id ? { ...n, isPublished: !n.isPublished } : n));
      toast.success("Notice status updated");
      triggerRefresh("notices");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await noticeAPI.delete(id);
      setNotices((prev) => prev.filter((n) => n._id !== id));
      setDeleteConfirm(null);
      toast.success("Notice deleted");
      triggerRefresh("notices");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete notice");
    }
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold text-[#1e3a8a]">Notices Management</h2>
          <p className="text-slate-500 text-sm mt-1">Create and manage school notices</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-[#1D4ED8] text-white font-semibold rounded-xl hover:bg-[#1e3a8a] transition-colors text-sm shadow-md hover:shadow-lg"
        >
          <FiPlus className="w-4 h-4" />
          New Notice
        </button>
      </div>

      {/* Search */}
      <div className="bg-white rounded-2xl border border-blue-100 p-4 mb-6 shadow-sm">
        <div className="relative">
          <FiSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search notices..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700 placeholder-blue-200"
          />
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-2xl border border-blue-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-blue-50/50 border-b border-blue-100">
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Title</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden sm:table-cell">Category</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider hidden md:table-cell">Date</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Status</th>
                  <th className="text-left px-5 py-3.5 text-slate-500 text-xs font-semibold uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((notice, idx) => (
                  <motion.tr
                    key={notice._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.04 }}
                    className="border-b border-blue-50 hover:bg-blue-50/30 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="font-semibold text-[#1e3a8a] text-sm truncate max-w-xs">{notice.title}</div>
                      <div className="text-xs text-slate-400 mt-0.5 max-w-xs truncate">{notice.content}</div>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${categoryColors[notice.category] || categoryColors.General}`}>
                        {notice.category}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-slate-400 text-xs">
                      {formatDate(notice.publishDate)}
                    </td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${notice.isPublished ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {notice.isPublished ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button onClick={() => openEdit(notice)}
                          className="p-1.5 text-slate-500 hover:text-[#1D4ED8] hover:bg-blue-50 rounded-lg transition-colors" title="Edit">
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button onClick={() => handleToggle(notice._id)}
                          className={`p-1.5 rounded-lg transition-colors ${notice.isPublished ? "text-amber-400 hover:bg-amber-50" : "text-emerald-400 hover:bg-emerald-50"}`}
                          title={notice.isPublished ? "Unpublish" : "Publish"}>
                          {notice.isPublished ? <FiEyeOff className="w-3.5 h-3.5" /> : <FiEye className="w-3.5 h-3.5" />}
                        </button>
                        <button onClick={() => setDeleteConfirm(notice._id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete">
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-14">
              <FiBell className="w-10 h-10 text-blue-200 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">No notices found</p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        title={editItem ? "Edit Notice" : "Create Notice"}
        size="lg"
        footer={
          <>
            <button onClick={() => setModal(false)}
              className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50 transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-[#1D4ED8] text-white rounded-xl text-sm font-semibold hover:bg-[#1e3a8a] transition-colors disabled:opacity-70">
              {saving && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
              {editItem ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
              placeholder="Notice title"
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-200 text-slate-700"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Category</label>
            <select
              value={form.category}
              onChange={(e) => setForm((p) => ({ ...p, category: e.target.value }))}
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none bg-white text-slate-700 focus:border-blue-400"
            >
              {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Publish Date</label>
              <input type="date" value={form.publishDate}
                onChange={(e) => setForm((p) => ({ ...p, publishDate: e.target.value }))}
                className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none text-slate-700 focus:border-blue-400"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Expiry Date</label>
              <input type="date" value={form.expiryDate}
                onChange={(e) => setForm((p) => ({ ...p, expiryDate: e.target.value }))}
                className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none text-slate-700 focus:border-blue-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1.5">Content *</label>
            <textarea
              value={form.content}
              onChange={(e) => setForm((p) => ({ ...p, content: e.target.value }))}
              rows={5}
              placeholder="Notice content..."
              className="w-full px-4 py-3 border border-blue-100 rounded-xl text-sm focus:outline-none resize-none text-slate-700 focus:border-blue-400 focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <label className="flex items-center gap-2.5 cursor-pointer">
            <input type="checkbox" checked={form.isPublished}
              onChange={(e) => setForm((p) => ({ ...p, isPublished: e.target.checked }))}
              className="w-4 h-4 rounded accent-blue-600"
            />
            <span className="text-sm text-slate-600">Publish immediately (visible on website)</span>
          </label>
        </div>
      </Modal>

      {/* Delete Confirm */}
      <Modal isOpen={!!deleteConfirm} onClose={() => setDeleteConfirm(null)} title="Confirm Delete" size="sm"
        footer={
          <>
            <button onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 border border-blue-100 text-slate-600 rounded-xl text-sm hover:bg-blue-50">
              Cancel
            </button>
            <button onClick={() => handleDelete(deleteConfirm)}
              className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-semibold hover:bg-red-600">
              Delete
            </button>
          </>
        }
      >
        <p className="text-slate-600 text-sm">Are you sure you want to delete this notice? This action cannot be undone.</p>
      </Modal>
    </AdminLayout>
  );
};

export default AdminNotices;
